import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { encontrarExcecaoEmConflito } from '../../../../natureza-operacao-excecao-escopo.util';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { PisExcecaoWizardDialogComponent, ExcecaoWizardDialogData, ExcecaoWizardDialogResult, GRUPOS_ESCOPO_DEF } from '../../excecao-wizard-dialog/excecao-wizard-dialog.component';
import { NaturezaOperacaoPisExcecaoPendenteStruct } from '@shared/services/structs/motor-fiscal/natureza-operacao-pis-excecao-pendente.struct';
import { CstPisStruct } from '@shared/services/structs/motor-fiscal/cst-pis.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { TipoEscopoEnum } from '@shared/enums/motor-fiscal/tipo-escopo.enum';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';
import { escopoLabels } from '../../../../escopo-contraparte.util';
import { MotorFiscalLookupService } from '@shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';

interface ExcecaoDisplayItem {
    _idx: number;
    escoposLabels: string[];
    cstPis: string | null;
    vigenciaTexto: string;
}

@Component({
    selector: 'app-pis-tributo-excecoes-section',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatTableModule,
    ],
    templateUrl: './tributo-excecoes-section.component.html',
    styleUrl: './tributo-excecoes-section.component.css',
})
export class PisTributoExcecoesSectionComponent implements OnChanges, OnInit {
    @Input() idNaturezaOperacao: number | null = null;
    @Input() codigoTributos: CstPisStruct[] = [];
    @Input() variaveis: VariavelStruct[] = [];
    @Input() formulasPis: FormulaCatalogoItemStruct[] = [];
    @Input() tipoOperacao: TipoOperacaoEnum = TipoOperacaoEnum.Saida;
    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];
    @Input() excecoesPendentes: NaturezaOperacaoPisExcecaoPendenteStruct[] = [];
    @Output() excecoesChange = new EventEmitter<NaturezaOperacaoPisExcecaoPendenteStruct[]>();

    public localExcecoes: NaturezaOperacaoPisExcecaoPendenteStruct[] = [];
    public tiposEscopoLabels: Record<number, string> = {};
    public displayedColumns = ['escopos', 'codigoTributo', 'vigencia', 'actions'];

    constructor(
        private dialog: MatDialog,
        private motorFiscalLookupService: MotorFiscalLookupService,
        private alertService: AlertService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['excecoesPendentes'] || changes['tipoOperacao']) {
            this.localExcecoes = [...this.excecoesPendentes];
            this.atualizarDisplay();
        }
    }

    ngOnInit(): void {
        this.motorFiscalLookupService.getTiposEscopo().subscribe(list => {
            this.tiposEscopoLabels = list.reduce((acc, item) => {
                acc[item.id] = item.descricao;
                return acc;
            }, {} as Record<number, string>);
            this.atualizarDisplay();
        });
    }

    /**
     * Lista exibida na tabela. Mantida como campo estável (recalculado apenas
     * quando os dados mudam) — um getter recriaria o array a cada ciclo de
     * detecção de mudanças, forçando o mat-table a re-renderizar todas as linhas
     * e fechando/esvaziando o menu de ações ao ser acionado.
     */
    public excoesDisplay: ExcecaoDisplayItem[] = [];

    private atualizarDisplay(): void {
        this.excoesDisplay = this.localExcecoes.map((p, i) => ({
            _idx: i,
            escoposLabels: this.escoposLabels(p.req.escopos),
            cstPis: p.req.cstPis ?? null,
            vigenciaTexto: this.formatarVigencia(p.req.dataVigenciaInicio, p.req.dataVigenciaFim),
        }));
    }

    /** Nomes distintos dos escopos presentes, na ordem em que aparecem. */
    private escoposLabels(escopos: { tipoEscopo: TipoEscopoEnum }[]): string[] {
        const labelMap = escopoLabels(GRUPOS_ESCOPO_DEF, this.tipoOperacao, this.tiposEscopoLabels);
        const labels: string[] = [];
        for (const e of escopos) {
            const lbl = labelMap[e.tipoEscopo] ?? e.tipoEscopo;
            if (!labels.includes(lbl)) labels.push(lbl);
        }
        return labels;
    }

    /** "dd/mm/yyyy - dd/mm/yyyy" ou "dd/mm/yyyy - Permanente". */
    private formatarVigencia(inicio: string, fim?: string | null): string {
        const ini = this.formatarData(inicio);
        const f = fim ? this.formatarData(fim) : 'Permanente';
        return `${ini} - ${f}`;
    }

    /** Converte "yyyy-MM-dd" (DateOnly) em "dd/MM/yyyy" sem deslocamento de fuso. */
    private formatarData(d: string): string {
        if (!d) return '';
        const [y, m, dia] = d.substring(0, 10).split('-');
        return `${dia}/${m}/${y}`;
    }

    public abrirWizardExcecao(): void {
        const data: ExcecaoWizardDialogData = {
            idNaturezaOperacao: this.idNaturezaOperacao ?? 0,
            idExcecao: null,
            excecaoData: null,
            codigoTributos: this.codigoTributos,
            variaveis: this.variaveis,
            formulas: this.formulasPis,
            tipoOperacao: this.tipoOperacao,
            modalidadesBcSt: this.modalidadesBcSt,
        };
        const ref = this.dialog.open(PisExcecaoWizardDialogComponent, {
            width: '960px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });
        ref.afterClosed().subscribe((result: ExcecaoWizardDialogResult | null) => {
            if (result?.salvo && result.excecao) {
                const conflito = encontrarExcecaoEmConflito(result.excecao, this.localExcecoes.map(e => e.req));
                if (conflito) {
                    const nome = conflito.nome?.trim() || 'Sem nome';
                    this.alertService.show('Exceção conflitante', 'Esta exceção possui escopos conflitantes e vigência sobreposta à exceção "' + nome + '", sem critério de desempate. Ajuste antes de salvar a natureza.', AlertType.warning);
                }
                this.localExcecoes = [...this.localExcecoes, { idExcecao: null, req: result.excecao }];
                this.atualizarDisplay();
                this.excecoesChange.emit([...this.localExcecoes]);
            }
        });
    }

    public abrirWizardEditar(idx: number): void {
        const pendente = this.localExcecoes[idx];
        if (!pendente) return;
        const data: ExcecaoWizardDialogData = {
            idNaturezaOperacao: this.idNaturezaOperacao ?? 0,
            idExcecao: pendente.idExcecao ?? null,
            excecaoData: pendente.req,
            codigoTributos: this.codigoTributos,
            variaveis: this.variaveis,
            formulas: this.formulasPis,
            tipoOperacao: this.tipoOperacao,
            modalidadesBcSt: this.modalidadesBcSt,
        };
        const ref = this.dialog.open(PisExcecaoWizardDialogComponent, {
            width: '960px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });
        ref.afterClosed().subscribe((result: ExcecaoWizardDialogResult | null) => {
            if (result?.salvo && result.excecao) {
                const conflito = encontrarExcecaoEmConflito(result.excecao, this.localExcecoes.filter((_, i) => i !== idx).map(e => e.req));
                if (conflito) {
                    const nome = conflito.nome?.trim() || 'Sem nome';
                    this.alertService.show('Exceção conflitante', 'Esta exceção possui escopos conflitantes e vigência sobreposta à exceção "' + nome + '", sem critério de desempate. Ajuste antes de salvar a natureza.', AlertType.warning);
                }
                const updated = [...this.localExcecoes];
                updated[idx] = { idExcecao: pendente.idExcecao, req: result.excecao };
                this.localExcecoes = updated;
                this.atualizarDisplay();
                this.excecoesChange.emit([...this.localExcecoes]);
            }
        });
    }

    public excluirExcecao(idx: number): void {
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar Exclusão',
                description: 'Você tem certeza que deseja excluir esta exceção? Esta ação não poderá ser desfeita.',
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar',
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.confirm) {
                this.localExcecoes = this.localExcecoes.filter((_, i) => i !== idx);
                this.atualizarDisplay();
                this.excecoesChange.emit([...this.localExcecoes]);
            }
        });
    }
}
